import App from "antd/es/app"
import Button from "antd/es/button"
import Divider from "antd/es/divider"
import Tooltip from "antd/es/tooltip"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  SquareStackIcon,
  Undo2Icon,
  WandSparklesIcon,
} from "lucide-react"
import { useCallback, useContext, useMemo, useState } from "react"

import { type LabelColumnsResult, labelQuestionnaireColumns } from "../../api"
import { getUserFacingError } from "../../errors"
import { getColumnLetter } from "../../sheets/utils"
import AdvancedMenu from "./AdvancedMenu"
import Column from "./Column"
import WizardContext from "./context"
import SheetProgress from "./SheetProgress"
import type { AnyDocument, DocumentFields, Field } from "./state"
import { isActiveColumn } from "./uiUtils"
import { isValidSheet } from "./utils"

const MapFieldsSheetBody: React.FC<{
  columns: string[]
  fields: DocumentFields
  activeSheetRows: string[][]
}> = ({ columns, fields, activeSheetRows }) => {
  return (
    <tbody>
      {activeSheetRows.map((row, key) => (
        <tr key={key}>
          <td className="bg-gray-25 w-12 border border-l-0 border-gray-100 p-1 text-center">
            {key + 1}
          </td>
          {row.map((cell, i) => {
            return (
              <td
                key={i}
                valign="top"
                className={`w-48 border border-gray-100 p-1 ${
                  isActiveColumn(columns[i], fields)
                    ? "bg-purple-400 font-bold text-white"
                    : ""
                }`}
              >
                <div>{cell}</div>
              </td>
            )
          })}
        </tr>
      ))}
    </tbody>
  )
}

interface StepMapFieldsProps {
  onClose: () => void
  onCancel: () => void
  handleDocumentProcessing: (document: AnyDocument) => Promise<void>
  hideResponseControls: boolean
}

const MapFields: React.FC<StepMapFieldsProps> = ({
  onClose,
  onCancel,
  handleDocumentProcessing,
  hideResponseControls,
}) => {
  const { state, dispatch } = useContext(WizardContext)
  const { documentIndex, documents } = state
  const document = documents[documentIndex]
  const { sheetIndex, workbookData } = document
  if (workbookData === undefined || workbookData.sheetNames.length < 1) {
    throw Error("Missing workbook data")
  }
  const sheet = state.documents[documentIndex].sheets[sheetIndex]
  if (!sheet) {
    throw Error("Missing sheet: " + sheetIndex.toString())
  }

  const { message: messageApi } = App.useApp()
  const { sheetName, fields } = sheet
  const [error, setError] = useState<string | undefined>(undefined)
  const [isAutoLabelLoading, setIsAutoLabelLoading] = useState(false)
  const [autoLabelError, setAutoLabelError] = useState("")
  const activeSheetRows = workbookData.sheetRows[sheetName]
  const [isSubmitting, setIsSubmitting] = useState(false)

  const numCols = activeSheetRows.length > 0 ? activeSheetRows[0].length : 0
  const columns = useMemo(() => {
    const columns: string[] = []
    for (let i = 0; i < numCols; i++) {
      columns.push(getColumnLetter(i + 1))
    }
    return columns
  }, [numCols])

  const serializeSheet = useCallback((rows: string[][]): string => {
    const truncateCell = (cell: string): string =>
      cell.length > 100 ? cell.slice(0, 50) + "..." + cell.slice(-50) : cell
    const rowsToUse = rows.slice(0, 15).map((r) => r.slice(0, 50))
    return rowsToUse
      .map((r) => {
        const parts = r.map((cell, i) => `${i + 1}=${truncateCell(cell)}`)
        return `| ${parts.join(" | ")} |`
      })
      .join("\n")
  }, [])

  const applySolverResult = useCallback(
    (result: LabelColumnsResult) => {
      const {
        question_id_index,
        primary_question_index,
        secondary_question_index,
        primary_answer_index,
        secondary_answer_index,
      } = result
      if (question_id_index != null) {
        dispatch({
          type: "SECTION_VALUE",
          documentIndex,
          sheetIndex,
          payload: {
            key: "questionId",
            value: columns[question_id_index - 1],
            index: question_id_index,
          },
        })
      }
      dispatch({
        type: "SECTION_VALUE",
        documentIndex,
        sheetIndex,
        payload: {
          key: "primaryQuestion",
          value: columns[primary_question_index - 1],
          index: primary_question_index,
        },
      })
      if (secondary_question_index != null) {
        dispatch({
          type: "SECTION_VALUE",
          documentIndex,
          sheetIndex,
          payload: {
            key: "secondaryQuestion",
            value: columns[secondary_question_index - 1],
            index: secondary_question_index,
          },
        })
      }
      // always treat primary answer as categorical
      dispatch({
        type: "SECTION_VALUE",
        documentIndex,
        sheetIndex,
        payload: {
          key: "answerCategorical",
          value: columns[primary_answer_index - 1],
          index: primary_answer_index,
        },
      })
      // optional explanation column
      if (secondary_answer_index != null) {
        dispatch({
          type: "SECTION_VALUE",
          documentIndex,
          sheetIndex,
          payload: {
            key: "answerExplanation",
            value: columns[secondary_answer_index - 1],
            index: secondary_answer_index,
          },
        })
      }
    },
    [dispatch, documentIndex, sheetIndex, columns],
  )

  const onAutoLabel = useCallback(async () => {
    setIsAutoLabelLoading(true)
    setAutoLabelError("")
    const noLabelMessage = "No column labels found"
    try {
      const sheetText = serializeSheet(activeSheetRows)
      const payload = await labelQuestionnaireColumns({ sheet: sheetText })
      applySolverResult(payload.result)
      if (Object.keys(payload.result).length === 0) {
        setAutoLabelError(noLabelMessage)
      }
    } catch (error) {
      console.error("Auto-label error:", error)
      const userFacingError = getUserFacingError(error)
      if (userFacingError.error === "INVALID_ARGUMENT") {
        setAutoLabelError(noLabelMessage)
      } else {
        setAutoLabelError("Auto-label failed, please try again later.")
      }
    } finally {
      setIsAutoLabelLoading(false)
    }
  }, [activeSheetRows, applySolverResult, serializeSheet])

  const onClickDone = useCallback(async () => {
    if (!document.sheets.some(isValidSheet)) {
      setError("Please select a primary question and answer")
      return
    }

    setError(undefined)
    setIsSubmitting(true)
    try {
      // TODO(mgraczyk): Show a message saying answers will appear in the answer
      // bank, but only from the non-answer bank view and not in the
      // questionnaire assistant.
      await handleDocumentProcessing(document)
      if (sheetIndex + 1 === document.sheets.length) {
        void messageApi.success("All questionnaire field layouts set.")
      } else {
        void messageApi.success("Questionnaire field layouts set.")
      }
      if (documentIndex + 1 < state.documents.length) {
        dispatch({ type: "DOCUMENT_INDEX", payload: documentIndex + 1 })
      } else {
        dispatch({ type: "DONE" })
        onClose()
      }
    } catch (e) {
      // TODO(mgraczyk): Better error handling and state management.
      console.error(e)
      setError("Could not upload questionnaire, try again later.")
      return
    } finally {
      setIsSubmitting(false)
    }
  }, [
    state,
    sheetIndex,
    dispatch,
    document,
    messageApi,
    documentIndex,
    onClose,
    handleDocumentProcessing,
  ])

  const isLastSheet = sheetIndex === document.sheets.length - 1
  const isInvalid = !document.sheets.some(isValidSheet)
  const onSkip = useCallback(async () => {
    setAutoLabelError("")
    dispatch({
      type: "CLEAR_FIELDS",
      documentIndex,
      sheetIndex,
    })
    if (isLastSheet) {
      if (!isInvalid) {
        await onClickDone()
      }
    } else {
      dispatch({
        type: "SHEET_INDEX",
        documentIndex,
        payload: sheetIndex + 1,
      })
    }
  }, [documentIndex, sheetIndex, dispatch, isLastSheet, isInvalid, onClickDone])

  const sheetCompleted = isValidSheet(sheet)
  const fieldsEmpty = Object.values(fields).every(
    (field: Field) => field.value === undefined,
  )
  const continueTitle = isInvalid
    ? "Please select a primary question and answer"
    : ""

  const advancedForm = hideResponseControls ? null : (
    <AdvancedMenu
      className="mb-4"
      documentIndex={documentIndex}
      currentDocument={document}
      dispatch={dispatch}
      showCollapsible
    />
  )

  const previousMCQOptions = useMemo(() => {
    for (let i = sheetIndex - 1; i >= 0; i--) {
      const prevSheet = document.sheets[i]
      if (prevSheet?.multipleChoices.length > 0) {
        return prevSheet.multipleChoices
      }
    }
    return []
  }, [document.sheets, sheetIndex])

  return (
    <>
      <div className="mb-2">
        <span className="text-semibold text-gray-800">
          Select a Question column and an Answer column.
        </span>{" "}
        <span className="text-gray-600">
          Or{" "}
          <strong
            className={isLastSheet ? "" : "cursor-pointer"}
            onClick={onSkip}
          >
            skip
          </strong>{" "}
          this sheet and Quilt will ignore it.
        </span>
        <Button
          onClick={onAutoLabel}
          className="mx-2 font-bold"
          disabled={isAutoLabelLoading}
          loading={isAutoLabelLoading}
          icon={<WandSparklesIcon />}
        >
          Auto label
        </Button>
        {autoLabelError && (
          <span className="text-red-500">{autoLabelError}</span>
        )}
      </div>
      {advancedForm}
      {error && <p className="text-red-500">{error}</p>}
      <div className="scrollbar-thumb-gray-300 scrollbar-track-gray-25 scrollbar-corner-gray-25 scrollbar-thin w-full grow overflow-auto border border-b-0 border-gray-100">
        <table className="min-w-full table-fixed">
          <thead className="sticky top-0">
            <tr>
              <th className="bg-gray-25 w-12 border-t-0 border-gray-100 text-gray-500">
                {" "}
              </th>
              {columns.map((column, idx) => (
                <Column
                  key={column}
                  column={column}
                  idx={idx}
                  documentIndex={documentIndex}
                  sheetIndex={sheetIndex}
                  sheet={sheet}
                  fields={fields}
                  dispatch={dispatch}
                  hideVerbositySelector={hideResponseControls}
                  existingMCQOptions={previousMCQOptions}
                />
              ))}
            </tr>
          </thead>
          <MapFieldsSheetBody
            columns={columns}
            fields={fields}
            activeSheetRows={activeSheetRows}
          />
        </table>
      </div>
      <SheetProgress />
      <div>
        <Divider />
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Button className="font-bold" onClick={onCancel}>
              Exit
            </Button>
            <Button
              onClick={() =>
                dispatch({ type: "CLEAR_FIELDS", documentIndex, sheetIndex })
              }
              disabled={fieldsEmpty}
              icon={<Undo2Icon />}
              className="font-bold"
            >
              Reset
            </Button>
            <Tooltip title="Reuse field mapping for all remaining sheets">
              <Button
                onClick={() =>
                  dispatch({
                    type: "REUSE_MAPPING",
                    sheetIndex,
                  })
                }
                disabled={fieldsEmpty}
                icon={<SquareStackIcon />}
                className="font-bold"
              >
                Reuse
              </Button>
            </Tooltip>
          </div>
          <div className="flex gap-2">
            {sheetIndex > 0 && (
              <Button
                onClick={() => {
                  dispatch({
                    type: "SHEET_INDEX",
                    documentIndex,
                    payload: Math.max(0, sheetIndex - 1),
                  })
                }}
                icon={<ArrowLeftIcon />}
                className="font-bold"
              >
                Back
              </Button>
            )}
            {!isLastSheet ? (
              <Button className="font-bold" onClick={onSkip}>
                Skip sheet
              </Button>
            ) : isLastSheet ? (
              <Button
                className="font-bold"
                disabled={isSubmitting || isInvalid}
                title={continueTitle}
                onClick={onSkip}
              >
                Skip sheet and be Done
              </Button>
            ) : null}
            {!isLastSheet ? (
              <Button
                type="primary"
                disabled={!sheetCompleted || isSubmitting}
                onClick={() => {
                  dispatch({
                    type: "SHEET_INDEX",
                    documentIndex,
                    payload: sheetIndex + 1,
                  })
                }}
                icon={<ArrowRightIcon />}
                title={continueTitle}
                className="font-bold"
              >
                Continue to the next sheet
              </Button>
            ) : (
              <Button
                type="primary"
                disabled={isInvalid || isSubmitting}
                onClick={onClickDone}
                icon={<CheckIcon />}
                title={continueTitle}
                className="font-bold"
              >
                {isSubmitting ? "Creating..." : "Done"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default MapFields
